// src/index.ts
import Select from "react-select";

// src/useAsyncPaginate.ts
import { useState as useState2, useCallback as useCallback2 } from "react";

// src/useAsyncPaginateBase.ts
import { useState, useEffect, useRef, useCallback } from "react";
import useIsMountedRef from "use-is-mounted-ref";
import { useLazyRef } from "@vtaits/use-lazy-ref";

// src/getInitialCache.ts
var getInitialCache = params => ({
  isFirstLoad: true,
  options: [],
  hasMore: true,
  isLoading: false,
  additional: params.additional
});

// src/getInitialOptionsCache.ts
var getInitialOptionsCache = _ref => {
  let {
    options,
    defaultOptions,
    additional,
    defaultAdditional
  } = _ref;
  const initialOptions = defaultOptions === true ? null : defaultOptions instanceof Array ? defaultOptions : options;
  if (initialOptions) {
    return {
      "": {
        isFirstLoad: false,
        isLoading: false,
        options: initialOptions,
        hasMore: true,
        additional: defaultAdditional || additional
      }
    };
  }
  return {};
};

// src/defaultShouldLoadMore.ts
var AVAILABLE_DELTA = 10;
var defaultShouldLoadMore = (scrollHeight, clientHeight, scrollTop) => {
  const bottomBorder = scrollHeight - clientHeight - AVAILABLE_DELTA;
  return bottomBorder < scrollTop;
};

// src/defaultReduceOptions.ts
var defaultReduceOptions = (prevOptions, loadedOptions) => [...prevOptions, ...loadedOptions];

// src/requestOptions.ts
import sleep from "sleep-promise";

// src/validateResponse.ts
var errorText = '[react-select-async-paginate] response of "loadOptions" should be an object with "options" prop, which contains array of options.';
var checkIsResponse = response => {
  if (!response) {
    return false;
  }
  const {
    options,
    hasMore
  } = response;
  if (!Array.isArray(options)) {
    return false;
  }
  if (typeof hasMore !== "boolean" && typeof hasMore !== "undefined") {
    return false;
  }
  return true;
};
var validateResponse = response => {
  if (!checkIsResponse(response)) {
    console.error(errorText, "Received:", response);
    throw new Error(errorText);
  }
  return true;
};

// src/requestOptions.ts
var requestOptions = async (caller, paramsRef, optionsCacheRef, debounceTimeout, setOptionsCache, reduceOptions) => {
  const currentInputValue = paramsRef.current.inputValue;
  const isCacheEmpty = !optionsCacheRef.current[currentInputValue];
  const currentOptions = isCacheEmpty ? getInitialCache(paramsRef.current) : optionsCacheRef.current[currentInputValue];
  if (currentOptions.isLoading || !currentOptions.hasMore) {
    return;
  }
  setOptionsCache(prevOptionsCache => ({
    ...prevOptionsCache,
    [currentInputValue]: {
      ...currentOptions,
      isLoading: true
    }
  }));
  if (debounceTimeout > 0 && caller === "input-change") {
    await sleep(debounceTimeout);
    const newInputValue = paramsRef.current.inputValue;
    if (currentInputValue !== newInputValue) {
      setOptionsCache(prevOptionsCache => {
        if (isCacheEmpty) {
          const {
            [currentInputValue]: itemForDelete,
            ...restCache
          } = prevOptionsCache;
          return restCache;
        }
        return {
          ...prevOptionsCache,
          [currentInputValue]: {
            ...currentOptions,
            isLoading: false
          }
        };
      });
      return;
    }
  }
  let response;
  let hasError = false;
  try {
    const {
      loadOptions
    } = paramsRef.current;
    response = await loadOptions(currentInputValue, currentOptions.options, currentOptions.additional);
  } catch (e) {
    hasError = true;
  }
  if (hasError) {
    setOptionsCache(prevOptionsCache => ({
      ...prevOptionsCache,
      [currentInputValue]: {
        ...currentOptions,
        isLoading: false
      }
    }));
    return;
  }
  if (validateResponse(response)) {
    const {
      options,
      hasMore
    } = response;
    const newAdditional = response.hasOwnProperty("additional") ? response.additional : currentOptions.additional;
    setOptionsCache(prevOptionsCache => ({
      ...prevOptionsCache,
      [currentInputValue]: {
        ...currentOptions,
        options: reduceOptions(currentOptions.options, options, newAdditional),
        hasMore: !!hasMore,
        isLoading: false,
        isFirstLoad: false,
        additional: newAdditional
      }
    }));
  }
};

// src/useAsyncPaginateBase.ts
var increaseStateId = prevStateId => prevStateId + 1;
var useAsyncPaginateBase = function (params) {
  let deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const {
    defaultOptions,
    loadOptionsOnMenuOpen = true,
    debounceTimeout = 0,
    inputValue,
    menuIsOpen,
    filterOption = null,
    reduceOptions = defaultReduceOptions,
    shouldLoadMore = defaultShouldLoadMore
  } = params;
  const isMountedRef = useIsMountedRef();
  const isInitRef = useRef(true);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const setStateId = useState(0)[1];
  const optionsCacheRef = useLazyRef(() => getInitialOptionsCache(params));
  const callRequestOptions = useCallback(caller => {
    requestOptions(caller, paramsRef, optionsCacheRef, debounceTimeout, reduceState => {
      optionsCacheRef.current = reduceState(optionsCacheRef.current);
      if (isMountedRef.current) {
        setStateId(increaseStateId);
      }
    }, reduceOptions);
  }, [debounceTimeout]);
  const handleScrolledToBottom = useCallback(() => {
    const currentInputValue = paramsRef.current.inputValue;
    const currentOptions2 = optionsCacheRef.current[currentInputValue];
    if (currentOptions2) {
      callRequestOptions("menu-scroll");
    }
  }, [callRequestOptions]);
  useEffect(() => {
    if (isInitRef.current) {
      isInitRef.current = false;
    } else {
      optionsCacheRef.current = {};
      setStateId(increaseStateId);
    }
    if (defaultOptions === true) {
      callRequestOptions("autoload");
    }
  }, deps);
  useEffect(() => {
    if (menuIsOpen && !optionsCacheRef.current[inputValue]) {
      callRequestOptions("input-change");
    }
  }, [inputValue]);
  useEffect(() => {
    if (menuIsOpen && !optionsCacheRef.current[""] && loadOptionsOnMenuOpen) {
      callRequestOptions("menu-toggle");
    }
  }, [menuIsOpen]);
  const currentOptions = optionsCacheRef.current[inputValue] || getInitialCache(params);
  return {
    handleScrolledToBottom,
    shouldLoadMore,
    filterOption,
    isLoading: currentOptions.isLoading,
    isFirstLoad: currentOptions.isFirstLoad,
    options: currentOptions.options
  };
};

// src/useAsyncPaginate.ts
var useAsyncPaginate = function (params) {
  let deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const {
    inputValue: inputValueParam,
    menuIsOpen: menuIsOpenParam,
    defaultInputValue: defaultInputValueParam,
    defaultMenuIsOpen: defaultMenuIsOpenParam,
    onInputChange: onInputChangeParam,
    onMenuClose: onMenuCloseParam,
    onMenuOpen: onMenuOpenParam
  } = params;
  const [inputValueState, setInputValue] = useState2(defaultInputValueParam || "");
  const [menuIsOpenState, setMenuIsOpen] = useState2(!!defaultMenuIsOpenParam);
  const inputValue = typeof inputValueParam === "string" ? inputValueParam : inputValueState;
  const menuIsOpen = typeof menuIsOpenParam === "boolean" ? menuIsOpenParam : menuIsOpenState;
  const onInputChange = useCallback2((nextInputValue, actionMeta) => {
    if (onInputChangeParam) {
      onInputChangeParam(nextInputValue, actionMeta);
    }
    setInputValue(nextInputValue);
  }, [onInputChangeParam]);
  const onMenuClose = useCallback2(() => {
    if (onMenuCloseParam) {
      onMenuCloseParam();
    }
    setMenuIsOpen(false);
  }, [onMenuCloseParam]);
  const onMenuOpen = useCallback2(() => {
    if (onMenuOpenParam) {
      onMenuOpenParam();
    }
    setMenuIsOpen(true);
  }, [onMenuOpenParam]);
  const baseResult = useAsyncPaginateBase({
    ...params,
    inputValue,
    menuIsOpen
  }, deps);
  return {
    ...baseResult,
    inputValue,
    menuIsOpen,
    onInputChange,
    onMenuClose,
    onMenuOpen
  };
};

// src/useComponents.ts
import { useMemo } from "react";
import { components as defaultComponents } from "react-select";

// src/wrapMenuList.tsx
import { useEffect as useEffect2, useRef as useRef2, useCallback as useCallback3 } from "react";
import composeRefs from "@seznam/compose-react-refs";
import { jsx } from "react/jsx-runtime";
var CHECK_TIMEOUT = 300;
function wrapMenuList(MenuList2) {
  function WrappedMenuList(props) {
    const {
      selectProps,
      innerRef
    } = props;
    const {
      handleScrolledToBottom,
      shouldLoadMore
    } = selectProps;
    const checkTimeoutRef = useRef2();
    const menuListRef = useRef2(null);
    const shouldHandle = useCallback3(() => {
      const el = menuListRef.current;
      if (!el) {
        return false;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = el;
      return shouldLoadMore(scrollHeight, clientHeight, scrollTop);
    }, [shouldLoadMore]);
    const checkAndHandle = useCallback3(() => {
      if (shouldHandle()) {
        if (handleScrolledToBottom) {
          handleScrolledToBottom();
        }
      }
    }, [shouldHandle, handleScrolledToBottom]);
    const setCheckAndHandleTimeout = useCallback3(() => {
      checkAndHandle();
      checkTimeoutRef.current = setTimeout(setCheckAndHandleTimeout, CHECK_TIMEOUT);
    }, [checkAndHandle]);
    useEffect2(() => {
      setCheckAndHandleTimeout();
      return () => {
        if (checkTimeoutRef.current) {
          clearTimeout(checkTimeoutRef.current);
        }
      };
    }, []);
    return /* @__PURE__ */jsx(MenuList2, {
      ...props,
      innerRef: composeRefs(innerRef, menuListRef)
    });
  }
  return WrappedMenuList;
}

// src/useComponents.ts
var MenuList = wrapMenuList(defaultComponents.MenuList);
var useComponents = components => useMemo(() => ({
  MenuList,
  ...components
}), [components]);

// src/withAsyncPaginate.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var defaultCacheUniqs = [];
var defaultComponents2 = {};
function withAsyncPaginate(SelectComponent) {
  function WithAsyncPaginate(props) {
    const {
      components = defaultComponents2,
      selectRef = void 0,
      isLoading: isLoadingProp,
      cacheUniqs = defaultCacheUniqs,
      ...rest
    } = props;
    const asyncPaginateProps = useAsyncPaginate(rest, cacheUniqs);
    const processedComponents = useComponents(components);
    const isLoading = typeof isLoadingProp === "boolean" ? isLoadingProp : asyncPaginateProps.isLoading;
    return /* @__PURE__ */jsx2(SelectComponent, {
      ...props,
      ...asyncPaginateProps,
      isLoading,
      components: processedComponents,
      ref: selectRef
    });
  }
  return WithAsyncPaginate;
}

// src/reduceGroupedOptions.ts
var checkGroup = group => {
  if (!group) {
    return false;
  }
  const {
    label,
    options
  } = group;
  if (typeof label !== "string" && typeof label !== "undefined") {
    return false;
  }
  if (!Array.isArray(options)) {
    return false;
  }
  return true;
};
var reduceGroupedOptions = (prevOptions, loadedOptions) => {
  const res = prevOptions.slice();
  const mapLabelToIndex = {};
  let prevOptionsIndex = 0;
  const prevOptionsLength = prevOptions.length;
  loadedOptions.forEach(optionOrGroup => {
    const group = checkGroup(optionOrGroup) ? optionOrGroup : {
      options: [optionOrGroup]
    };
    const {
      label = ""
    } = group;
    let groupIndex = mapLabelToIndex[label];
    if (typeof groupIndex !== "number") {
      for (; prevOptionsIndex < prevOptionsLength && typeof mapLabelToIndex[label] !== "number"; ++prevOptionsIndex) {
        const prevGroup = prevOptions[prevOptionsIndex];
        if (checkGroup(prevGroup)) {
          mapLabelToIndex[prevGroup.label || ""] = prevOptionsIndex;
        }
      }
      groupIndex = mapLabelToIndex[label];
    }
    if (typeof groupIndex !== "number") {
      mapLabelToIndex[label] = res.length;
      res.push(group);
      return;
    }
    res[groupIndex] = {
      ...res[groupIndex],
      options: [...res[groupIndex].options, ...group.options]
    };
  });
  return res;
};

// src/index.ts
var AsyncPaginate = withAsyncPaginate(Select);
export { AsyncPaginate, checkIsResponse, reduceGroupedOptions, useAsyncPaginate, useAsyncPaginateBase, useComponents, validateResponse, withAsyncPaginate, wrapMenuList };